import { Icon, NonIdealState, Spinner } from "@blueprintjs/core";
import {
  useGetMediaDownloadUrlQuery,
  useGetMediaQuery,
} from "@package/ui-gql/index-data-access";
import { getMediaType } from "@package/ui-wall-engine";
import { useMemo } from "react";
import { ReactPlayerErrorBoundaryRetry } from "../ReactPlayerErrorBoundaryRetry/ReactPlayerErrorBoundaryRetry";
import "./MediaImage.css";

type MediaTagProps = {
  id?: string | null;
  size?:
    | "max-h-32"
    | "max-h-48"
    | "max-h-64"
    | "max-h-80"
    | "max-h-96"
    | "max-h-full";
};

export function MediaImage({ id, size = "max-h-96" }: MediaTagProps) {
  const {
    data: dataMedia,
    loading: loadingMedia,
    error: errorMedia,
  } = useGetMediaQuery({
    variables: { id: id ?? "" },
    skip: !id,
  });

  const file = useMemo<string | null | undefined>(() => {
    if (!dataMedia?.item?.isDir) {
      return dataMedia?.item?.file;
    }

    return undefined;
  }, [dataMedia?.item?.file, dataMedia?.item?.isDir]);

  const {
    data: dataMediaUrl,
    loading: loadingMediaUrl,
    error: errorMediaUrl,
  } = useGetMediaDownloadUrlQuery({
    variables: {
      fileName: file ?? "",
    },
    skip: !file,
  });

  if (!id) {
    return null;
  }

  if (dataMedia?.item?.isDir) {
    return <Icon icon="folder-close" size={64} />;
  }

  if (loadingMedia || loadingMediaUrl) {
    return <Spinner size={100} />;
  }

  if (errorMedia || errorMediaUrl || !dataMediaUrl?.item?.url) {
    return (
      <NonIdealState icon="warning-sign" title="Failed loading media preview" />
    );
  }

  if (!dataMedia) {
    return null;
  }

  if (getMediaType(dataMedia.item) === "image") {
    return (
      <img
        className={`${size} w-fit`}
        src={dataMediaUrl.item.url}
        alt={dataMedia?.item?.name}
      />
    );
  }

  return (
    <ReactPlayerErrorBoundaryRetry
      className="media-image-component"
      style={{ maxHeight: 384 }}
      width="100%"
      height="100%"
      light
      loop={false}
      muted
      playing
      url={dataMediaUrl.item.url}
    />
  );
}
